import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as roundsApi from '../api/rounds';

export const useRounds = (gameId, params = {}) => {
  return useQuery({
    queryKey: ['rounds', gameId, params],
    queryFn: () => roundsApi.getRounds(gameId, params),
    enabled: !!gameId,
  });
};

export const useCurrentRound = (gameId) => {
  return useQuery({
    queryKey: ['currentRound', gameId],
    queryFn: () => roundsApi.getCurrentRound(gameId),
    enabled: !!gameId,
    // Real-time updates via WebSocket (useGameSocket hook)
  });
};

export const useRound = (gameId, roundId) => {
  return useQuery({
    queryKey: ['round', gameId, roundId],
    queryFn: () => roundsApi.getRound(gameId, roundId),
    enabled: !!gameId && !!roundId,
  });
};

export const useCreateRound = (gameId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roundData) => roundsApi.createRound(gameId, roundData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rounds', gameId] });
      queryClient.invalidateQueries({ queryKey: ['currentRound', gameId] });
      queryClient.invalidateQueries({ queryKey: ['game', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
      // Invalidate all influence queries (GM may have been topped off)
      queryClient.invalidateQueries({ queryKey: ['influence', 'me', gameId] });
      queryClient.invalidateQueries({ queryKey: ['influence', 'balances', gameId] });
    },
  });
};

export const useSetRoundPrompt = (gameId, roundId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (promptData) => roundsApi.setRoundPrompt(gameId, roundId, promptData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['round', gameId, roundId] });
      queryClient.invalidateQueries({ queryKey: ['currentRound', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
    },
  });
};

export const useUpdateRoundSettings = (gameId, roundId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings) => roundsApi.updateRoundSettings(gameId, roundId, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['round', gameId, roundId] });
      queryClient.invalidateQueries({ queryKey: ['currentRound', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
      queryClient.invalidateQueries({ queryKey: ['influence', gameId] });
    },
  });
};

export const useAdvanceRound = (gameId, roundId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => roundsApi.advanceRound(gameId, roundId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['round', gameId, roundId] });
      queryClient.invalidateQueries({ queryKey: ['currentRound', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
      queryClient.invalidateQueries({ queryKey: ['influence', gameId] });
    },
  });
};

export const useAddRoundAspect = (gameId, roundId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (aspectData) => roundsApi.addRoundAspect(gameId, roundId, aspectData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['round', gameId, roundId] });
      queryClient.invalidateQueries({ queryKey: ['currentRound', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
      queryClient.invalidateQueries({ queryKey: ['scenes', gameId] });
      queryClient.invalidateQueries({ queryKey: ['influence', gameId] });
    },
  });
};

export const useCompleteRound = (gameId, roundId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => roundsApi.completeRound(gameId, roundId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['round', gameId, roundId] });
      queryClient.invalidateQueries({ queryKey: ['currentRound', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
      queryClient.invalidateQueries({ queryKey: ['roundResults', gameId, roundId] });
      queryClient.invalidateQueries({ queryKey: ['influence', gameId] });
    },
  });
};

export const useAwardGMPick = (gameId, roundId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId) => roundsApi.awardGMPick(gameId, roundId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['round', gameId, roundId] });
      queryClient.invalidateQueries({ queryKey: ['currentRound', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
      queryClient.invalidateQueries({ queryKey: ['roundResults', gameId, roundId] });
      queryClient.invalidateQueries({ queryKey: ['influence', gameId] });
      queryClient.invalidateQueries({ queryKey: ['passages', gameId] });
    },
  });
};

export const useRoundResults = (gameId, roundId) => {
  return useQuery({
    queryKey: ['roundResults', gameId, roundId],
    queryFn: () => roundsApi.getRoundResults(gameId, roundId),
    enabled: !!gameId && !!roundId,
  });
};

export const useReadyStatus = (gameId, roundId, phase) => {
  return useQuery({
    queryKey: ['readyStatus', gameId, roundId],
    queryFn: () => roundsApi.getReadyStatus(gameId, roundId),
    enabled: !!gameId && !!roundId && phase === 'influence',
    // Refresh periodically for real-time feel (socket will also update)
    refetchInterval: 5000,
  });
};

export const useToggleReady = (gameId, roundId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => roundsApi.togglePlayerReady(gameId, roundId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readyStatus', gameId, roundId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
    },
  });
};
